import type { Todo } from "../../domain/entities.ts";
import { addDays } from "./recurrence.ts";
import { taskTimelineRange, validTimelineDate } from "./collection-timeline.ts";
import { resolveWallTime, shiftWallTime, taskTimeMismatch } from "./task-time.ts";

export type TimelineOperation = "move" | "start" | "end";
export const timelineScheduleFields = ["startDate", "dueDate", "startAt", "endAt", "wallStartAt", "wallEndAt", "estimatedMinutes"] as const;
export type TimelineSchedule = Pick<Todo, typeof timelineScheduleFields[number]>;

export function timelineScheduleKey(todo: Todo): string {
  return JSON.stringify([...timelineScheduleFields.map(field => todo[field] ?? null), todo.scheduleMode || "fixed", todo.scheduleTimeZone || null, !!todo.checkpoint, !!todo.deleted, todo.archivedAt || null, todo.rootId, todo.categoryId, todo.parentTaskId || null]);
}

export function timelineResizable(todo: Todo): boolean {
  return !todo.checkpoint && (!!todo.startDate && !!todo.dueDate || !!todo.startAt && !!todo.endAt);
}

export function planTimelineSchedule(todo: Todo, start: string, due: string | null): TimelineSchedule {
  if(taskTimelineRange(todo))throw new Error("This task already has dates. Select its bar to change them.");
  if(!validTimelineDate(start)||due!==null&&!validTimelineDate(due))throw new Error("Choose a valid start date and optional deadline");
  if(due&&due<start)throw new Error("Start date must not be after the deadline");
  if(taskTimeMismatch(todo))throw new Error("Review this task’s clock times in the editor before scheduling it");
  return {startDate:start,dueDate:due,startAt:null,endAt:null,wallStartAt:null,wallEndAt:null,estimatedMinutes:todo.estimatedMinutes??null};
}

export function planTimelineChange(todo: Todo, operation: TimelineOperation, days: number): TimelineSchedule {
  const range = taskTimelineRange(todo);
  if (!range || range.invalid) throw new Error("Review this task’s dates before moving it");
  if (!Number.isInteger(days) || Math.abs(days) > 3650) throw new Error("Choose a whole-day change between −3650 and 3650 days");
  if (operation !== "move" && !timelineResizable(todo)) throw new Error("Resize requires a non-checkpoint task with both start and end dates or times");
  if (taskTimeMismatch(todo)) throw new Error("Review the task’s clock times in the editor before moving it");
  const next: TimelineSchedule = { startDate:todo.startDate || null, dueDate:todo.dueDate || null, startAt:todo.startAt || null, endAt:todo.endAt || null, wallStartAt:todo.wallStartAt || null, wallEndAt:todo.wallEndAt || null, estimatedMinutes:todo.estimatedMinutes || null };
  const date = (value: string | null | undefined) => {
    if (!value) return null;
    const shifted = addDays(value, days);
    if (!validTimelineDate(shifted) || shifted.length !== 10) throw new Error("The proposed date is outside the supported calendar");
    return shifted;
  };
  const instant = (value: string | null | undefined) => value ? new Date(Date.parse(value) + days * 86400000).toISOString() : null;
  if (operation !== "end") {
    next.startDate = date(todo.startDate);
    if (todo.scheduleMode === "floating") { next.wallStartAt=shiftWallTime(todo.wallStartAt, days); next.startAt=next.wallStartAt ? resolveWallTime(next.wallStartAt,todo.scheduleTimeZone!) : null; }
    else next.startAt=instant(todo.startAt);
  }
  if (operation !== "start") {
    next.dueDate=date(todo.dueDate);
    if (todo.scheduleMode === "floating") { next.wallEndAt=shiftWallTime(todo.wallEndAt,days); next.endAt=next.wallEndAt ? resolveWallTime(next.wallEndAt,todo.scheduleTimeZone!) : null; }
    else next.endAt=instant(todo.endAt);
  }
  if (next.startDate && next.dueDate && next.startDate > next.dueDate) throw new Error("Start date must not be after the due date");
  if (next.startAt && next.endAt && Date.parse(next.endAt) <= Date.parse(next.startAt)) throw new Error("End time must be after the start time");
  if (operation !== "move" && next.startAt && next.endAt) {
    next.estimatedMinutes=Math.round((Date.parse(next.endAt)-Date.parse(next.startAt))/60000);
    if (next.estimatedMinutes > 100000) throw new Error("The resulting timed duration exceeds 100,000 minutes");
  }
  return next;
}

export function timelineWarnings(todo: Todo, next: TimelineSchedule, tasks: Todo[]): string[] {
  const warnings: string[] = [];
  const range=taskTimelineRange({...todo,...next});
  for (const id of todo.dependencyIds || []) {
    const blocker=tasks.find(task=>task.id===id && task.rootId===todo.rootId && !task.deleted && !task.archivedAt);
    if (!blocker) { warnings.push("A dependency is unavailable on this device; verify its dates before proceeding."); continue; }
    if (blocker.completed) continue;
    const blockerRange=taskTimelineRange(blocker);
    if (blocker.endAt && next.startAt ? Date.parse(blocker.endAt)>Date.parse(next.startAt) : !!blockerRange && !!range && blockerRange.end>=range.start) warnings.push(`May start before “${blocker.title || "Untitled task"}” finishes.`);
  }
  if (next.startAt && next.endAt && tasks.some(other=>other.id!==todo.id && !other.deleted && !other.archivedAt && !other.completed && other.startAt && other.endAt && (other.assignedTo || []).some(id=>(todo.assignedTo || []).includes(id)) && Date.parse(other.startAt)<Date.parse(next.endAt!) && Date.parse(other.endAt)>Date.parse(next.startAt!))) warnings.push("Overlaps timed work assigned to the same person.");
  if (todo.recurrence) warnings.push("Only this task occurrence changes. Repeat rules and other occurrences are not rescheduled.");
  if (todo.startAt || todo.endAt) warnings.push(todo.scheduleMode === "floating" ? `Clock times stay in ${todo.scheduleTimeZone}; resolved instants may shift at daylight-saving changes.` : "Fixed times move by elapsed 24-hour days; local clock display can change across daylight-saving boundaries.");
  return [...new Set(warnings)];
}
